<template>
    <div class="how-page">
        <section class="how-hero text-center py-5">
            <div class="container py-lg-5">
                <p class="section-eyebrow mx-auto">{{ $t('marketing.how.eyebrow') }}</p>
                <h1 class="display-4 fw-bolder text-gray-900 mx-auto">{{ $t('marketing.how.title') }}</h1>
                <p class="fs-5 text-gray-600 mx-auto mb-4">{{ $t('marketing.how.subtitle') }}</p>
                <router-link to="/login" class="btn btn-brand btn-lg text-white fw-bold px-5 py-3">{{ $t('marketing.cta.primary') }}</router-link>
                <p class="small text-gray-500 mx-auto mt-3">{{ $t('marketing.cta.reassurance') }}</p>
            </div>
        </section>

        <section class="container py-5">
            <div class="row g-4">
                <div v-for="(step, index) in steps" :key="step.title" class="col-12 col-md-6">
                    <article class="step-card h-100 p-4 p-md-5">
                        <span class="step-number">{{ index + 1 }}</span>
                        <h2 class="h3 fw-bolder mt-4">{{ step.title }}</h2>
                        <p class="fs-5 text-gray-600 mb-0">{{ step.description }}</p>
                    </article>
                </div>
            </div>
        </section>

        <section class="details-section py-5">
            <div class="container py-lg-4">
                <div class="row align-items-center gy-5">
                    <div class="col-12 col-lg-6">
                        <h2 class="display-6 fw-bolder text-gray-900">{{ $t('marketing.how.details_title') }}</h2>
                        <p class="fs-5 text-gray-600">{{ $t('marketing.how.details_subtitle') }}</p>
                        <div class="row g-3 mt-3">
                            <div v-for="detail in details" :key="detail" class="col-12 col-sm-6">
                                <p class="d-flex gap-2 mb-0">
                                    <BootstrapIcon class="flex-shrink-0 mt-1" color="fill-teal-600 text-teal-600" name="check-circle-fill" :size="17" />
                                    <span>{{ detail }}</span>
                                </p>
                            </div>
                        </div>
                    </div>
                    <div class="col-12 col-lg-6">
                        <img class="w-100 product-image" :src="'/img/marketing/' + locale + '-bakery-orders.png'" :alt="$t('marketing.home.workflow.image_alt')">
                    </div>
                </div>
            </div>
        </section>

        <section class="container py-5">
            <div class="row align-items-center gy-5">
                <div class="col-12 col-lg-6 order-lg-2">
                    <h2 class="display-6 fw-bolder text-gray-900">{{ $t('marketing.how.status_title') }}</h2>
                    <p class="fs-5 text-gray-600">{{ $t('marketing.how.status_subtitle') }}</p>
                    <div class="d-flex flex-wrap gap-2 mt-4">
                        <span class="status status-pending">{{ $t('console.order_status.pending') }}</span>
                        <span class="status status-preparing">{{ $t('console.order_status.preparing') }}</span>
                        <span class="status status-completed">{{ $t('console.order_status.completed') }}</span>
                        <span class="status status-handed">{{ $t('console.order_status.handed') }}</span>
                    </div>
                </div>
                <div class="col-6 col-lg-3 order-lg-0 text-center">
                    <img class="mobile-image" :src="'/img/bank/' + locale + '_tapgiant-mobile-order-dashboard.png'" alt="">
                </div>
                <div class="col-6 col-lg-3 order-lg-1 text-center">
                    <img class="mobile-image" :src="'/img/bank/' + locale + '_tapgiant-mobile-order-summary.png'" alt="">
                </div>
            </div>
        </section>

        <section class="history-section py-5">
            <div class="container py-lg-4 text-center">
                <BootstrapIcon class="mb-3" color="fill-teal-600 text-teal-600" name="clock-history" :size="42" />
                <h2 class="display-6 fw-bolder text-gray-900">{{ $t('marketing.how.history_title') }}</h2>
                <p class="fs-5 text-gray-600 mx-auto">{{ $t('marketing.how.history_subtitle') }}</p>
            </div>
        </section>

        <section class="container py-5 text-center">
            <h2 class="display-6 fw-bolder text-gray-900">{{ $t('marketing.how.final_title') }}</h2>
            <router-link to="/login" class="btn btn-brand btn-lg text-white fw-bold px-5 py-3 mt-3">{{ $t('marketing.cta.primary') }}</router-link>
        </section>
    </div>
</template>

<script>
import BootstrapIcon from '@/common/directive/BootstrapIcon.vue'

export default {
    components: { BootstrapIcon },
    computed: {
        locale: function () { return this.$i18n.locale },
        steps: function () {
            return [1, 2, 3, 4].map(index => ({
                title: this.$t(`marketing.how.steps.${index}.title`),
                description: this.$t(`marketing.how.steps.${index}.description`)
            }))
        },
        details: function () {
            return [1, 2, 3, 4, 5, 6].map(index => this.$t(`marketing.how.details.${index}`))
        }
    }
}
</script>

<style scoped lang="scss">
.how-hero { background: linear-gradient(145deg, #fff 0%, #f0faf7 100%); margin-top: -30px; }
.how-hero h1, .how-hero p, .history-section p { max-width: 820px; }
.section-eyebrow { color: #16856a; font-size: .78rem; font-weight: 800; letter-spacing: .12em; text-transform: uppercase; }
.step-card { border: 1px solid #e5ecea; border-radius: 18px; }
.step-number { align-items: center; background: #d9f7ee; border-radius: 50%; color: #126d58; display: flex; font-size: 1.1rem; font-weight: 800; height: 44px; justify-content: center; width: 44px; }
.details-section, .history-section { background: #f7faf9; }
.product-image { filter: drop-shadow(0 20px 30px rgba(35, 61, 55, .16)); }
.mobile-image { filter: drop-shadow(0 18px 22px rgba(35, 61, 55, .14)); max-width: 100%; }
.status { border-radius: 99px; font-size: .8rem; font-weight: 800; padding: 8px 13px; }
.status-pending { background: #fff0e8; color: #b64216; }
.status-preparing { background: #d9f7ee; color: #126d58; }
.status-completed { background: #dff5fb; color: #08738d; }
.status-handed { background: #f1f5cf; color: #65710b; }
@media (max-width: 767.98px) { .how-hero .display-4 { font-size: 2.4rem; } }
</style>
